import { useEffect } from "react";
import {
  CssBaseline,
  ThemeProvider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { deDE, enUS, isIS } from "@mui/x-date-pickers/locales";
import type { Locale } from "date-fns";
import { de as deLocale } from "date-fns/locale/de";
import { enUS as enLocale } from "date-fns/locale/en-US";
import { is as isLocale } from "date-fns/locale/is";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { theme } from "./theme";
import { LangProvider, useLang } from "./i18n/LangContext";
import type { Lang } from "./types";
import { AppStateProvider } from "./state/AppState";
import { Topbar } from "./components/Topbar";
import { Footer } from "./components/Footer";
import { HomePage } from "./pages/HomePage";
import { MapPage } from "./pages/MapPage";
import { BlogIndexPage } from "./pages/BlogIndexPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { ImpressumPage } from "./pages/ImpressumPage";
import { NotFoundPage } from "./pages/NotFoundPage";

const DATE_LOCALES: Record<Lang, Locale> = {
  de: deLocale,
  en: enLocale,
  is: isLocale,
};

const PICKER_LOCALE_TEXT: Record<Lang, object> = {
  de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
  en: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
  is: isIS.components.MuiLocalizationProvider.defaultProps.localeText,
};

/** Legacy hash-URL redirector: keep old `/#/spot/foo` deep-links working
 *  after the switch from HashRouter to BrowserRouter. Runs once on mount
 *  and on every location change (in case a stray link sneaks in). */
function LegacyHashRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#/")) {
      const dest = hash.slice(1);
      navigate(dest, { replace: true });
    }
  }, [navigate, location.pathname]);
  return null;
}

/** Scroll to top on every route change for content pages — the map
 *  manages its own scroll position; content pages should always start
 *  at the top after a navigation. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/map") || pathname.startsWith("/spot/")) return;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const { lang } = useLang();
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={DATE_LOCALES[lang]}
      localeText={PICKER_LOCALE_TEXT[lang]}
    >
      <AppStateProvider>
        <LegacyHashRedirect />
        <ScrollToTop />
        <Topbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/filter" element={<MapPage />} />
          <Route path="/spot/:spotId" element={<MapPage />} />
          <Route path="/spot/:spotId/filter" element={<MapPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Footer />
      </AppStateProvider>
    </LocalizationProvider>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline enableColorScheme />
          <LangProvider>
            <AppRoutes />
          </LangProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
